// ============= Imports =============
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Modal,
  Spacer,
  SearchBarWithDropdown,
  Dropdown,
} from '@tw/tw-component-library';
import { Button, Heading } from '@tw/tw-component-library/common-wt';
import {
  PaymentWorkflowType,
  getWorkflowTypeFromDisplayName,
  getWorkflowTypeName,
  requiresClientMatterData,
} from '../forms/types/api/graph.types';
import { filterMobileRestrictedWorkflowTypes } from '../../../utils/mobileRestrictions';
import { getOfficeForWorkflowType } from '../../../utils/workflowRegistry';
import styled from 'styled-components';

const RequestContainer = styled.div`
  max-width: 600px;
  width: 100%;
`;

// ============= Types =============
interface CreateRequestModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onClose: () => void;
  clientData: string[];
  matterData: string[];
  selectedClientLabel?: string;
  onClientSearch: (value: string) => void;
  onMatterSearch: (value: string) => void;
  onClientSelect: (option: string) => void;
  onMatterSelect: (option: string) => void;
  onCreateRequest: (workflowType: PaymentWorkflowType) => void;
  isLoadingClients: boolean;
  isLoadingMatters: boolean;
  showWorkflowTypeSelect?: boolean;
  allowedWorkflowTypes?: PaymentWorkflowType[];
  currentWorkflow?: {
    name: string;
    icon: string;
    description: string;
    path: string;
  } | null;
  isDashboardMode?: boolean; // New prop to determine if this is dashboard mode
  onWorkflowTypeChange?: (workflowType: PaymentWorkflowType) => void; // Callback for workflow type changes
  onOfficeChange?: (office: string) => void; // Callback for office changes
}

// ============= Component =============
export const CreateRequestModal: React.FC<CreateRequestModalProps> = ({
  isOpen,
  onClose,
  clientData,
  matterData,
  onClientSearch,
  onMatterSearch,
  onClientSelect,
  onMatterSelect,
  onCreateRequest,
  isLoadingClients,
  isLoadingMatters,
  showWorkflowTypeSelect = false,
  allowedWorkflowTypes,
  currentWorkflow,
  isDashboardMode = false,
  onWorkflowTypeChange,
  onOfficeChange,
}) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMatter, setSelectedMatter] = useState('');
  const [selectedWorkflowType, setSelectedWorkflowType] =
    useState<PaymentWorkflowType>(() => {
      if (currentWorkflow?.name) {
        return getWorkflowTypeFromDisplayName(currentWorkflow.name);
      }
      return PaymentWorkflowType.Undefined;
    });
  const [showMatterDropdown, setShowMatterDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredMatterData, setFilteredMatterData] = useState<string[]>([]);

  const isClientMatterSelectionEnabled = useMemo(() => {
    if (!isDashboardMode) return true;
    if (selectedWorkflowType === PaymentWorkflowType.Undefined) return false;
    return requiresClientMatterData(selectedWorkflowType);
  }, [isDashboardMode, selectedWorkflowType]);

  const skipClientMatterSelection =
    selectedWorkflowType !== PaymentWorkflowType.Undefined &&
    !requiresClientMatterData(selectedWorkflowType);

  const resetState = useCallback(() => {
    setSelectedClient('');
    setSelectedMatter('');
    setSelectedWorkflowType(() => {
      if (currentWorkflow?.name) {
        return getWorkflowTypeFromDisplayName(currentWorkflow.name);
      }
      return PaymentWorkflowType.Undefined;
    });
    setShowMatterDropdown(false);
    setShowClientDropdown(false);
    setLoading(false);
    setFilteredMatterData([]);
  }, [currentWorkflow?.name]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!selectedClient) {
      setShowMatterDropdown(false);
      setSelectedMatter('');
    }
  }, [selectedClient]);

  useEffect(() => {
    if (currentWorkflow?.name) {
      setSelectedWorkflowType(
        getWorkflowTypeFromDisplayName(currentWorkflow.name)
      );
    }
  }, [currentWorkflow?.name]);

  // Ensure selected workflow type is allowed
  useEffect(() => {
    if (!allowedWorkflowTypes || allowedWorkflowTypes.length === 0) return;
    setSelectedWorkflowType((prev) => {
      if (allowedWorkflowTypes.includes(prev)) return prev;
      // pick the first allowed workflow type
      return allowedWorkflowTypes[0] as unknown as PaymentWorkflowType;
    });
  }, [allowedWorkflowTypes]);

  // Update filtered matter data when matter data changes (after database search)
  useEffect(() => {
    if (selectedClient && matterData.length > 0) {
      setFilteredMatterData(matterData);
    }
  }, [matterData, selectedClient]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleClientSelect = (option: string | null) => {
    // Disable selection if client/matter selection is not enabled
    if (!isClientMatterSelectionEnabled) return;

    if (option) {
      setSelectedClient(option);
      onClientSelect(option); // Only call parent handler with string value
      setShowClientDropdown(false);
      setTimeout(() => setShowMatterDropdown(true), 100);
      // When a client is selected, set filtered matters to current matter data
      setFilteredMatterData(matterData);
    } else {
      setSelectedClient('');
      // Don't call parent handler with null
      setShowMatterDropdown(false);
      setFilteredMatterData([]);
    }
  };

  const handleClientSearch = (value: string) => {
    // Disable search if client/matter selection is not enabled
    if (!isClientMatterSelectionEnabled) return;

    // If user types back to the selected label, do not reopen/search
    if (value === selectedClient) {
      setShowClientDropdown(false);
      return;
    }
    if (!value) {
      setSelectedMatter('');
      setShowMatterDropdown(false);
    }
    onClientSearch(value);
  };

  const handleMatterSearch = (value: string) => {
    // Disable search if client/matter selection is not enabled
    if (!isClientMatterSelectionEnabled) return;

    // If user types back to the selected label, do not reopen/search
    if (value === selectedMatter) {
      setShowMatterDropdown(false);
      return;
    }

    if (!value) {
      setSelectedClient('');
      setShowClientDropdown(false);
      setFilteredMatterData([]);
      return;
    }

    // If we have a selected client, filter the existing matter data instead of searching the database
    if (selectedClient) {
      const filtered = matterData.filter((matter) =>
        matter.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredMatterData(filtered);
    } else {
      // No client selected, so search the database
      onMatterSearch(value);
    }
  };

  const handleMatterSelect = (option: string | null) => {
    // Disable selection if client/matter selection is not enabled
    if (!isClientMatterSelectionEnabled) return;

    if (option) {
      setSelectedMatter(option);
      onMatterSelect(option); // Only call parent handler with string value
      setShowMatterDropdown(false);
      setTimeout(() => setShowClientDropdown(true), 100);
    } else {
      setSelectedMatter('');
      // Don't call parent handler with null
      setShowClientDropdown(false);
    }
  };

  const getDisplayText = (option: string) => option;

  const workflowTypeOptions = useMemo(() => {
    const allOptions = Object.entries(PaymentWorkflowType)
      .filter(([, value]) => typeof value === 'number')
      .map(([, value]) => ({
        label: getWorkflowTypeName(value as PaymentWorkflowType),
        value: value.toString(),
        enumValue: value,
      }));

    // First filter by allowed workflow types
    const allowedOptions =
      allowedWorkflowTypes && allowedWorkflowTypes.length > 0
        ? allOptions.filter((opt) =>
            allowedWorkflowTypes.includes(opt.enumValue as PaymentWorkflowType)
          )
        : allOptions;

    // Then filter out mobile-restricted workflow types
    const filteredAllowedTypes =
      allowedWorkflowTypes && allowedWorkflowTypes.length > 0
        ? filterMobileRestrictedWorkflowTypes(allowedWorkflowTypes)
        : filterMobileRestrictedWorkflowTypes(
            Object.values(PaymentWorkflowType).filter(
              (v) => typeof v === 'number'
            ) as PaymentWorkflowType[]
          );

    return allowedOptions.filter((opt) =>
      filteredAllowedTypes.includes(opt.enumValue as PaymentWorkflowType)
    );
  }, [allowedWorkflowTypes]);

  const handleWorkflowTypeChange = (value: string) => {
    const newWorkflowType = Number(value) as PaymentWorkflowType;
    setSelectedWorkflowType(newWorkflowType);

    // Call the parent callback to update workflow type
    onWorkflowTypeChange?.(newWorkflowType);

    // In dashboard mode, also update the office and clear client/matter selection
    if (isDashboardMode) {
      const office = getOfficeForWorkflowType(newWorkflowType);
      onOfficeChange?.(office);

      setSelectedClient('');
      setSelectedMatter('');
      setShowClientDropdown(false);
      setShowMatterDropdown(false);
    }
  };

  const handleCreateRequest = async () => {
    setLoading(true);
    await onCreateRequest(selectedWorkflowType);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create a new request"
      size="large"
      height="500px"
      focusFirstInput
      footer={
        <Button
          size="sm"
          onClick={() => {
            if (selectedWorkflowType === PaymentWorkflowType.Undefined) {
              return;
            }
            if (
              !requiresClientMatterData(selectedWorkflowType) ||
              (selectedClient && selectedMatter)
            ) {
              handleCreateRequest();
            }
          }}
          isLoading={loading}
          disabled={
            selectedWorkflowType === PaymentWorkflowType.Undefined ||
            (requiresClientMatterData(selectedWorkflowType) &&
              (!selectedClient ||
                !selectedMatter ||
                !isClientMatterSelectionEnabled))
          }
        >
          Create Request
        </Button>
      }
    >
      <Heading level={3}>Request Details</Heading>
      <Spacer />
      <RequestContainer>
        {/* Show workflow selector first in dashboard mode */}
        {isDashboardMode && (
          <>
            <Dropdown
              label="Select Workflow Type"
              options={workflowTypeOptions}
              selectedValue={selectedWorkflowType.toString()}
              onChange={handleWorkflowTypeChange}
            />
            <Spacer />
          </>
        )}

        {/* Show workflow selector in workflow mode if needed */}
        {!isDashboardMode && showWorkflowTypeSelect && (
          <>
            <Dropdown
              label="Workflow Type"
              options={workflowTypeOptions}
              selectedValue={selectedWorkflowType.toString()}
              onChange={handleWorkflowTypeChange}
            />
            <Spacer />
          </>
        )}

        <SearchBarWithDropdown<string>
          options={clientData}
          placeholder={
            skipClientMatterSelection
              ? 'Not required for this workflow type'
              : isClientMatterSelectionEnabled
              ? 'Client...'
              : 'Select workflow type first...'
          }
          onSelect={handleClientSelect}
          onSearch={handleClientSearch}
          searchButton={false}
          value={selectedClient}
          isOpenOverride={showClientDropdown}
          setIsOpenOverride={setShowClientDropdown}
          isLoading={isLoadingClients}
          getDisplayText={getDisplayText}
          disabled={skipClientMatterSelection || !isClientMatterSelectionEnabled}
        />
        <Spacer />
        <SearchBarWithDropdown<string>
          options={selectedClient ? filteredMatterData : matterData}
          placeholder={
            skipClientMatterSelection
              ? 'Not required for this workflow type'
              : isClientMatterSelectionEnabled
              ? 'Matter...'
              : 'Select workflow type first...'
          }
          onSelect={handleMatterSelect}
          onSearch={handleMatterSearch}
          searchButton={false}
          value={selectedMatter}
          isOpenOverride={showMatterDropdown}
          setIsOpenOverride={setShowMatterDropdown}
          isLoading={isLoadingMatters}
          getDisplayText={getDisplayText}
          disabled={skipClientMatterSelection || !isClientMatterSelectionEnabled}
        />
        <Spacer />
      </RequestContainer>
    </Modal>
  );
};
